import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary as BaseErrorBoundary, FallbackProps } from 'react-error-boundary';
import { usePrevious } from 'react-use';

import { useTranslate } from '@growtherapy/i18n';
import { Alert, DRAWER_TITLE_ID, DrawerBody, IconButton, cn } from '@growtherapy/sprout-ui';

function FallbackComponent(_props: FallbackProps) {
  const t = useTranslate();

  return (
    <DrawerBody>
      <Alert
        className='mt-16'
        heading={t('telehealth-core:errorsCore.somethingWentWrongPleaseTryAgainLater')}
        use='error'
        headingAs='h2'
      />
    </DrawerBody>
  );
}

function ErrorBoundary({ children, onError }: { children: ReactNode; onError?: (error: Error) => void }) {
  return (
    <BaseErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
      {children}
    </BaseErrorBoundary>
  );
}

export type PushDrawerProps = {
  id: string;
  isOpen: boolean;
  isDarkTheme: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: 'left' | 'right';
  className?: string;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
  /** Called when the drawer's content throws; the fallback UI is shown regardless. */
  onError?: (error: Error) => void;
};

export function PushDrawer({
  id,
  isOpen = true,
  isDarkTheme,
  onClose,
  children,
  placement = 'right',
  className,
  closeButtonRef: closeButtonRefProp,
  onError,
}: PushDrawerProps) {
  const t = useTranslate();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const prevChildren = usePrevious(children);

  useEffect(
    function syncShouldRenderWithOpen() {
      if (isOpen) {
        setShouldRender(isOpen);
      }
    },
    [isOpen],
  );

  if (!shouldRender) {
    return <></>;
  }

  return (
    <aside
      aria-labelledby={DRAWER_TITLE_ID}
      className={cn(
        'shrink border border-neutral-800 bg-neutral-300 flex flex-col isolate', // general styling regardless of screen size
        'rebrand:border-border-default rebrand:bg-surface-default',
        'max-sm:z-20 max-sm:fixed top-0 bottom-0 max-h-full h-screen w-screen', // mobile styling
        'sm:top-3 sm:bottom-3 sm:rounded-xl sm:h-full sm:max-w-[42rem]', // larger than mobile styling
        {
          'rebrand:border-none rebrand:bg-surface-inverseEmphasis': isDarkTheme,
          'animate-slide-up sm:animate-slide-in-right right-0 sm:right-3': isOpen && placement === 'right',
          'animate-slide-down sm:animate-slide-out-left': !isOpen && placement === 'right',
          'animate-slide-up sm:animate-slide-in-left left-3': isOpen && placement === 'left',
          'animate-slide-down sm:animate-slide-out-right': !isOpen && placement === 'left',
          'transition-all max-sm:opacity-5 sm:w-0': !isOpen,
        },
        className,
      )}
      id={id}
      onAnimationEnd={(): void => {
        if (!isOpen) {
          setShouldRender(false);
        } else {
          closeButtonRefProp.current?.focus();
        }
      }}
    >
      <IconButton
        ref={closeButtonRefProp}
        aria-label={t('telehealth-core:chatDrawer.closeDrawer')}
        iconDefinition={faXmark}
        onClick={onClose}
        className={cn(
          'absolute right-0 my-4 mx-6 h-8 w-8 flex-none items-center justify-center sm:my-6 sm:mx-8 md:mx-10 md:my-8',
          'rebrand:min-h-component-125 rebrand:min-w-component-125 rebrand:m-250 rebrand:md:mx-450',
          {
            hidden: !isOpen,
            'rebrand:text-text-inverse': isDarkTheme,
          },
        )}
        use='chromeless'
        size='lg'
      />
      <ErrorBoundary onError={onError}>{isOpen ? children : prevChildren}</ErrorBoundary>
    </aside>
  );
}

export type PushDrawerContainerProps = Pick<PushDrawerProps, 'children' | 'className' | 'isDarkTheme'>;

export function PushDrawerContainer({ children, className, isDarkTheme }: PushDrawerContainerProps) {
  return (
    <div
      className={cn(
        { 'border-t border-b rebrand:border-border-default': !isDarkTheme },
        'flex flex-row gap-2 overflow-hidden h-full w-full',
        className,
      )}
    >
      {children}
    </div>
  );
}
